import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

interface HeatmapData {
  hot_numbers: { number: number; count: number }[]
  cold_numbers: { number: number; count: number }[]
  frequency_map: Record<number, number>
  total_draws: number
  analysis_period_years: number
}

const YEAR_OPTIONS = [1, 2, 3, 5, 10] as const

function HeatmapGrid({ freqMap, maxCount }: { freqMap: Record<number, number>; maxCount: number }) {
  const numbers = Array.from({ length: 99 }, (_, i) => i + 1)

  function getColor(count: number): { bg: string; fg: string } {
    if (maxCount === 0 || count === 0) return { bg: 'var(--heat-0)', fg: 'var(--color-text-soft)' }
    const ratio = count / maxCount
    if (ratio > 0.75) return { bg: '#5d6b50', fg: '#fff' }
    if (ratio > 0.5)  return { bg: '#7a8a6a', fg: '#fff' }
    if (ratio > 0.25) return { bg: '#a4b399', fg: '#2d2820' }
    return { bg: '#d4dec5', fg: '#5a5f50' }
  }

  return (
    <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(11, 1fr)' }}>
      {numbers.map(n => {
        const c = getColor(freqMap[n] ?? 0)
        return (
          <div key={n}
               className="aspect-square rounded-md flex items-center justify-center text-[10px] font-bold"
               style={{ background: c.bg, color: c.fg }}
               title={`${n}: ออก ${freqMap[n] ?? 0} ครั้ง`}>
            {n.toString().padStart(2, '0')}
          </div>
        )
      })}
    </div>
  )
}

export default function HeatmapPage() {
  const navigate = useNavigate()
  const [years, setYears] = useState<typeof YEAR_OPTIONS[number]>(1)

  const { data, isLoading, error } = useQuery({
    queryKey: ['heatmap', 'government', years],
    queryFn: () => api.lottery.heatmap('government', years) as Promise<HeatmapData>,
    staleTime: 1000 * 60 * 60,
  })

  const maxCount = data ? Math.max(...Object.values(data.frequency_map)) : 0

  return (
    <div className="flex flex-col min-h-dvh p-4 pb-6" style={{ background: 'var(--color-bg)' }}>

      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate('/')} style={{ color: 'var(--color-text-muted)' }}>
          <ChevronLeft size={24} />
        </button>
        <div>
          <h1 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>
            📊 เลขเด่นตามสถิติ
          </h1>
          <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            ข้อมูลเลขร้อน (ออกเยอะ) • เลขเย็น (ออกน้อย)
          </p>
        </div>
      </div>

      {/* Year Selector — Dropdown */}
      <div className="mb-4 card-amulet p-3 flex items-center justify-between">
        <div className="text-[11px] font-semibold" style={{ color: 'var(--color-gold-dark)' }}>
          📅 ย้อนหลัง
        </div>
        <div className="relative">
          <select
            value={years}
            onChange={e => setYears(Number(e.target.value) as typeof YEAR_OPTIONS[number])}
            className="appearance-none pl-3 pr-8 py-2 rounded-xl text-sm font-bold outline-none cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, #7a8a6a 0%, #5d6b50 100%)',
              color: '#fff',
              border: 'none',
              boxShadow: '0 2px 8px rgba(122, 138, 106, 0.3)',
            }}>
            {YEAR_OPTIONS.map(y => (
              <option key={y} value={y} style={{ background: '#5d6b50', color: '#fff' }}>
                {y} ปีล่าสุด
              </option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                       style={{ color: '#fff' }} />
        </div>
      </div>

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="text-4xl animate-pulse">📊</div>
          <p style={{ color: 'var(--color-text-muted)' }}>กำลังวิเคราะห์ข้อมูล...</p>
        </div>
      )}

      {error && (
        <div className="text-center py-20">
          <p style={{ color: 'var(--color-danger)' }}>ไม่สามารถโหลดข้อมูลได้</p>
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-4">
          {/* Info + Legend */}
          <div className="card-amulet p-3 flex items-center justify-between">
            <p className="text-xs font-semibold" style={{ color: 'var(--color-gold-dark)' }}>
              วิเคราะห์จาก {data.total_draws} งวด • {years} ปี
            </p>
            <div className="flex gap-2 text-[10px]">
              <Legend color="#d4dec5" label="น้อย" />
              <Legend color="#a4b399" label="ปานกลาง" />
              <Legend color="#7a8a6a" label="มาแรง" />
              <Legend color="#5d6b50" label="มากสุด" />
            </div>
          </div>

          {/* Grid */}
          <div className="card-amulet p-3">
            <HeatmapGrid freqMap={data.frequency_map} maxCount={maxCount} />
          </div>

          {/* Top 10 มาแรง */}
          <div className="card-amulet p-4">
            <h3 className="font-bold mb-3 text-sm flex items-center gap-2"
                style={{ color: '#5d6b50' }}>
              <span className="text-base">🌿</span> เลขมาแรง Top 10
              <span className="text-[10px] font-normal ml-auto"
                    style={{ color: 'var(--color-text-soft)' }}>
                {years} ปีล่าสุด
              </span>
            </h3>
            <div className="flex flex-wrap gap-2">
              {data.hot_numbers.map(({ number, count }) => (
                <div key={number} className="flex flex-col items-center">
                  <span className="w-11 h-11 rounded-xl flex items-center justify-center font-bold text-sm"
                        style={{
                          background: 'linear-gradient(135deg, #7a8a6a 0%, #5d6b50 100%)',
                          color: '#fff',
                          boxShadow: '0 2px 6px rgba(122, 138, 106, 0.35)',
                        }}>
                    {number.toString().padStart(2, '0')}
                  </span>
                  <span className="text-[10px] mt-0.5 text-center" style={{ color: 'var(--color-text-muted)' }}>
                    ({count} ครั้ง)
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Top 10 เย็น */}
          <div className="card-amulet p-4">
            <h3 className="font-bold mb-3 text-sm flex items-center gap-2"
                style={{ color: 'var(--color-text-muted)' }}>
              <span className="text-base">🧊</span> เลขเย็น Top 10
              <span className="text-[10px] font-normal ml-auto"
                    style={{ color: 'var(--color-text-soft)' }}>
                {years} ปีล่าสุด
              </span>
            </h3>
            <div className="flex flex-wrap gap-2">
              {data.cold_numbers.map(({ number, count }) => (
                <div key={number} className="flex flex-col items-center">
                  <span className="w-11 h-11 rounded-xl flex items-center justify-center font-bold text-sm"
                        style={{
                          background: 'var(--color-surface-2)',
                          color: 'var(--color-text-muted)',
                          border: '1px solid var(--color-surface-3)',
                        }}>
                    {number.toString().padStart(2, '0')}
                  </span>
                  <span className="text-[10px] mt-0.5" style={{ color: 'var(--color-text-soft)' }}>
                    {count} ครั้ง
                  </span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-[10px] pb-2" style={{ color: 'var(--color-text-soft)' }}>
            ข้อมูลเพื่อการศึกษาเท่านั้น ไม่ใช่คำแนะนำทางการเงิน
          </p>
        </div>
      )}
    </div>
  )
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
    </span>
  )
}
